import { Forbidden } from '@risksmart-app/components/src/errors/errors';
import { useGetOptionalGuidParam } from '@risksmart-app/components/src/routes/routes.utils';
import { ObligationType as Obligation_Type_Enum } from '@risksmart-app/domain/src/types/consts/obligation-type';
import { ParentTypes as Parent_Type_Enum } from '@risksmart-app/domain/src/types/consts/parent-type';
import type { FC } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { ownerContributorAndViewerIds } from 'src/components/form';
import type { ObligationFormFieldData } from 'src/pages/compliance/obligations/forms/obligationSchema';
import { defaultValues } from 'src/pages/compliance/obligations/forms/obligationSchema';
import { useHasPermissionQuery } from 'src/rbac/useHasPermission';

import Loading from '@/components/loading';
import { useInsertObligation } from '@/hooks/mutations/obligation/useInsertObligation';
import { useGetObligationById } from '@/hooks/queries';
import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { useIsFormPrefillEnabled } from '@/hooks/useIsFormPrefillEnabled';
import { obligationDetailsUrl } from '@/utils/urls';

import ObligationDetailsForm from '../../../forms/ObligationDetailsForm';

/**
 * Create tab for both create routes:
 *
 * - `/compliance/obligation` — parentless. The gate is type-level, so it renders
 *   read-only for a user without a type-level insert grant. Unchanged.
 * - `/compliance/obligation/:obligationId/add` — parent-scoped (COI-642),
 *   reached from the dashboard's per-parent Add. The gate resolves
 *   instance-level against the parent, which is how a user whose insert
 *   capability comes from an ownership grant gets here. Mirrors
 *   `pages/risks/create/tabs/RiskCreateTab.tsx`.
 */
const Tab: FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const typeParam = searchParams.get('type') || '';
  const parentObligationIdParam = useGetOptionalGuidParam('obligationId');

  const {
    data: parentObligationData,
    loading: parentObligationLoading,
    error: parentObligationError,
  } = useGetObligationById({
    queryArgs: { id: parentObligationIdParam ?? '' },
    shouldSkip: !parentObligationIdParam,
  });
  const parentObligation = parentObligationData?.obligation?.[0] ?? null;

  // Derive the permitted child type from the parent's Type, not from the URL
  // query param. The typeParam is an untrusted hint; hierarchy authority comes
  // from the parent obligation.
  const childTypeFromParent =
    parentObligation?.Type === Obligation_Type_Enum.Standard
      ? Obligation_Type_Enum.Chapter
      : parentObligation?.Type === Obligation_Type_Enum.Chapter
        ? Obligation_Type_Enum.Rule
        : parentObligation?.Type === Obligation_Type_Enum.Rule
          ? Obligation_Type_Enum.Task
          : null;

  // With a parent, this resolves instance-level (`canInsertChild`) and the third
  // argument is ignored; without one it keeps the parentless route's existing
  // type-level behaviour.
  const {
    hasPermission: canCreateObligation,
    loading: canCreateObligationLoading,
  } = useHasPermissionQuery('insert:obligation', parentObligation, true);

  const { insertObligation } = useInsertObligation();
  const objectToolbarEnabled = useIsFeatureFlagEnabled('dynamic_forms');
  const formPrefillEnabled = useIsFormPrefillEnabled();

  // Parent-scoped route only: fail loudly rather than rendering a read-only form
  // for a parent the caller can't insert under (matches RiskCreateTab).
  if (
    parentObligationIdParam &&
    (parentObligationLoading || canCreateObligationLoading)
  ) {
    return <Loading />;
  }

  if (parentObligationIdParam && parentObligationError) {
    throw new Forbidden('Access denied');
  }

  if (parentObligationIdParam && !parentObligation) {
    throw new Forbidden('Parent obligation not found');
  }

  if (parentObligationIdParam && !canCreateObligation) {
    throw new Forbidden('Access denied');
  }

  const onSave = async ({
    ancestorContributors: _1,
    Contributors,
    Owners,
    Viewers,
    departments,
    tags,
    ...data
  }: ObligationFormFieldData) => {
    // When a parent is provided, derive Type from the parent's actual Type
    // (never from the URL param), and use the parent's Id from the route.
    // This ensures the saved ParentId/Type pair is authoritative.
    const savePayload = {
      ...data,
      // Override Type and ParentId when a parent is present
      Type: parentObligation ? childTypeFromParent! : data.Type,
      ParentId: parentObligation ? parentObligation.Id : data.ParentId,
      DepartmentTypeIds: departments?.map((d) => d.DepartmentTypeId) || [],
      TagTypeIds: tags?.map((t) => t.TagTypeId) || [],
      ...ownerContributorAndViewerIds({ Contributors, Owners, Viewers }),
      CustomAttributeData: data.CustomAttributeData,
      Adherence: data.Adherence ?? '',
    };

    const result = await insertObligation(savePayload);
    const obligationId = result?.insertChildObligation?.Id;
    if (!obligationId) {
      throw new Error('Obligation id is missing');
    }
    const destination = obligationDetailsUrl(obligationId);

    return {
      savedIds: [obligationId],
      createdEntity: { id: obligationId, destination },
      successNavigation: { destination, isReplace: true },
    };
  };
  const onDismiss = (saved?: boolean) => {
    if (!saved) {
      navigate(-1);
    }
  };

  return (
    <>
      <ObligationDetailsForm
        initialType={
          // When a parent is present, derive type from the parent's Type
          // (hierarchy authority). Otherwise use the typeParam as a hint
          // (but it's not locked, so the user can still choose).
          parentObligation
            ? childTypeFromParent!
            : Object.values<string>(Obligation_Type_Enum).includes(typeParam)
              ? (typeParam as Obligation_Type_Enum)
              : undefined
        }
        initialParentId={parentObligationIdParam}
        // The parent came from the route and the type from the column that
        // linked here — neither is the user's to change on this surface, and
        // switching type would clear the locked parent (the Type radio's
        // onChange nulls ParentId).
        typeAndParentLocked={!!parentObligationIdParam}
        // Guarantees the locked parent renders with a label even when the
        // parent-type option list doesn't contain it; the list's real
        // title-labelled entry still wins when it does.
        parentObligationNode={
          parentObligation
            ? {
                Id: parentObligation.Id,
                SequentialId: parentObligation.SequentialId,
                ObjectType: Parent_Type_Enum.Obligation,
              }
            : null
        }
        defaultValues={defaultValues}
        onSave={onSave}
        onDismiss={onDismiss}
        readOnly={!canCreateObligationLoading && !canCreateObligation}
        publishToolbarState={objectToolbarEnabled}
        publishActiveForm={formPrefillEnabled}
      />
    </>
  );
};

export default Tab;
